/**
 * batch-66 verkoop-extractie (FO-493) - deterministische lezer van de
 * Finqle-factuuropmaak (FA2026-20437 / FA2026-20439). Puur: alleen de PDF-tekst
 * in, gestructureerde velden uit. Geen DB, geen AI.
 *
 * Wat de opmaak lastig maakt (en waarom dit niet aan de AI alleen wordt gelaten):
 *   - de btw-tabel en "Totaal te betalen" staan in de tekst VOOR de kop;
 *   - "Uitgiftedatum" is de factuurdatum, "PO:" de referentie ("-" = leeg),
 *     "Registratienummer" het KvK-nummer en "BTW nummer: N/A" is leeg;
 *   - het blok "Debiteur:" is de klant; "Besteld door:" is contact/afleveradres;
 *   - het afzenderblok (de STREEP-BV) staat onderaan, bij de voettekst;
 *   - het IBAN in de betalingsinstructies is van de factoringmaatschappij en
 *     nooit van de klant of de BV;
 *   - de tekstlaag bevat losse kerning-spaties ("Uitgifte datum : 08- 09- 2026").
 *
 * Alles wordt daarom op de GEPLETTE regel gelezen (zie `tekst.ts`).
 */

import type { ExtractieRegelParser, GestructureerdeRegelSoort } from '@streep/types'
import {
  losseRegexBron,
  naamSleutel,
  normaliseerKlokTijd,
  normaliseerWitruimte,
  parseNlBedragNaarCenten,
  parseNlDatum,
  parseNlGetalNaarDuizendsten,
  plet,
  pletKlein,
  splitsRegels,
} from './tekst'

/** Een partij op de factuur (debiteur of afzender). */
export interface FinqlePartij {
  naam: string | null
  /** Adresregels onder de naam (zonder btw/registratienummer). */
  adres: string[]
  /** Btw-nummer zoals het er staat; null bij "N/A" of een leeg veld. */
  btwNummer: string | null
  /** KvK-nummer (8 cijfers) uit "Registratienummer"; null als het geen 8 cijfers zijn. */
  kvk: string | null
}

/** Eén rij van de btw-tarieftabel. */
export interface FinqleBtwRij {
  percentage: number
  grondslagCenten: number
  btwCenten: number
  inclCenten: number
}

/** Een factuurregel zoals hij in de tekst staat (bedragen in centen, eenheden in duizendsten). */
export interface FinqleTekstRegel {
  soort: GestructureerdeRegelSoort
  /** De omschrijving zoals in de tekst (met kerning-spaties, regels samengevoegd). */
  omschrijvingRuw: string
  /** Dezelfde omschrijving geplet (vergelijk-vorm). */
  omschrijvingPlat: string
  medewerkerRuw: string | null
  medewerkerSleutel: string | null
  datum: string | null
  datumTot: string | null
  startTijd: string | null
  eindTijd: string | null
  pauzeMinuten: number | null
  tarieftypeRuw: string | null
  dienstRuw: string | null
  werkbonNummer: string | null
  eenhedenDuizendsten: number | null
  eenheid: 'uur' | 'dag' | 'km' | null
  tariefCenten: number | null
  totaalExCenten: number
  btwPercentage: number | null
  btwCenten: number | null
  totaalInclCenten: number | null
}

export interface FinqleTotalen {
  exCenten: number | null
  btwCenten: number | null
  inclCenten: number | null
}

/** Uitkomst van de consistentiecontroles op de tekst. */
export interface FinqleConsistentie {
  /** excl + btw = incl (1 cent speling) op de eindtotalen. */
  totalenSluiten: boolean | null
  /** "Totaal te betalen" is gelijk aan het totaal incl. */
  totaalTeBetalenKlopt: boolean | null
  /** De som van de regels (excl.) is gelijk aan het subtotaal. */
  regelsSluiten: boolean | null
  /** Elke regel: eenheden x tarief = totaal excl. (1 cent speling). */
  regelBedragenKloppen: boolean | null
}

export interface FinqleTekstExtractie {
  factuurnummer: string | null
  project: string | null
  /** PO-nummer; null bij "-" of een leeg veld. */
  po: string | null
  /** Het label "PO:" staat op de factuur (dan is een lege PO een FEIT, geen gemis). */
  poAanwezig: boolean
  factuurdatum: string | null
  vervaldatum: string | null
  betalingstermijnDagen: number | null
  debiteur: FinqlePartij | null
  besteldDoor: { naam: string | null; tav: string | null } | null
  afzender: FinqlePartij | null
  btwRijen: FinqleBtwRij[]
  totaalTeBetalenCenten: number | null
  /** De regel "Totalen:" (excl / btw / incl). */
  totalen: FinqleTotalen
  /** "Subtotalen (eenheden)" en "Subtotalen (kosten)". */
  subtotaalEenheden: (FinqleTotalen & { eenhedenDuizendsten: number | null }) | null
  subtotaalKosten: FinqleTotalen | null
  regels: FinqleTekstRegel[]
  /** De tekst noemt factoring (cessie aan een factoringmaatschappij). */
  factoring: boolean
  /** Het IBAN uit de betalingsinstructies (van de factoringmaatschappij). */
  factoringIban: string | null
  factoringTenNameVan: string | null
  consistentie: FinqleConsistentie
}

/**
 * De cijfers van een bedrag ("3.259,56"). Alle herhalingen zijn BEGRENSD: een
 * regel met een lange rij cijfers uit een geprepareerde PDF mag de regex nooit
 * kwadratisch laten terugzoeken (de tekst komt uit een open mailbox).
 */
const CIJFERS = '(?:\\d{1,3}(?:\\.\\d{3}){1,4}|\\d{1,12}),\\d{2}'
/** Bedrag zonder valutateken, met een optioneel minteken ervoor. */
const BEDRAG = `-?${CIJFERS}`
/**
 * Bedrag met een optioneel euroteken. Het teken zit IN de capture: "€3.259,56",
 * "€-3.259,56" (zo schrijft nl-NL een creditbedrag) en "-€3.259,56". Bewust geen
 * minteken achteraan: in een rij aaneengesloten bedragen is dat niet te
 * onderscheiden van het minteken voor het volgende bedrag.
 */
const EURO_BEDRAG = `(-?€?-?${CIJFERS})`
const DATUM = '\\d{1,2}-\\d{1,2}-\\d{4}'
const WERKBON = '[A-Za-z]{2,6}\\d{6,9}'

/** Bedragenregel van de eenhedentabel: eenheden, tarief, totaal ex, btw %, btw, totaal in. */
const EENHEDEN_BEDRAGEN_RE = new RegExp(
  `^(-?(?:\\d{1,3}(?:\\.\\d{3}){1,3}|\\d{1,9})(?:,\\d{1,3})?)${EURO_BEDRAG}${EURO_BEDRAG}(\\d{1,2}(?:,\\d{1,2})?)%${EURO_BEDRAG}${EURO_BEDRAG}$`,
)
/** Staart van een kostenregel: totaal ex, btw %, btw, totaal in. */
const KOSTEN_STAART_RE = new RegExp(
  `${EURO_BEDRAG}(\\d{1,2}(?:,\\d{1,2})?)%${EURO_BEDRAG}${EURO_BEDRAG}$`,
)
const BTW_RIJ_RE = new RegExp(
  `^BTW-(\\d{1,2}(?:,\\d{1,2})?)%${EURO_BEDRAG}${EURO_BEDRAG}${EURO_BEDRAG}$`,
  'i',
)
const DRIE_BEDRAGEN_RE = new RegExp(`${EURO_BEDRAG}${EURO_BEDRAG}${EURO_BEDRAG}$`)
/**
 * "Totaal te betalen: € 3.944,08". Links VERANKERD aan het label: staat er iets
 * tussen dat de lezer niet kent (een ander minteken dan "-", een toevoeging),
 * dan wordt het bedrag niet gelezen in plaats van als positief overgenomen.
 */
const TOTAAL_TE_BETALEN_RE = new RegExp(`^totaaltebetalen:?${EURO_BEDRAG}$`, 'i')

const KOP_EENHEDEN = pletKlein('Beschrijving:Eenheden:Tarief:Totaal ex:BTW %:BTW:Totaal in:')
const KOP_KOSTEN = pletKlein('Datum:Beschrijving:Totaal ex:BTW %:BTW:Totaal in:')

function centen(waarde: string | undefined): number | null {
  return parseNlBedragNaarCenten(waarde ?? null)
}

function percentage(waarde: string | undefined): number | null {
  const duizendsten = parseNlGetalNaarDuizendsten(waarde ?? null)
  return duizendsten === null ? null : duizendsten / 1000
}

/** "N/A", "n.v.t.", "-", "geen", "onbekend" of leeg: het veld is niet ingevuld. */
export function isLegeWaardeAanduiding(waarde: string | null | undefined): boolean {
  if (waarde == null) return true
  const kaal = pletKlein(waarde).replace(/[.\-_/]/g, '')
  return kaal === '' || ['na', 'nvt', 'geen', 'onbekend', 'nb', 'none', 'null'].includes(kaal)
}

/** Waarde achter een label op een geplette regel ("PO:OR2507343" -> "OR2507343"). */
function waardeNaLabel(plat: string, label: string): string | null {
  const m = new RegExp(`^${label}:?(.*)$`, 'i').exec(plat)
  return m ? (m[1] ?? '') : null
}

interface Regel {
  ruw: string
  plat: string
  klein: string
  /**
   * Alleen bij een te lange regel (zie MAX_REGEL_LENGTE): de geplette tekst in
   * kleine letters. Uitsluitend voor `includes` (lineair), nooit voor een regex.
   */
  lang?: string
}

function isBlokStart(klein: string): boolean {
  return (
    klein === 'debiteur:' ||
    klein === 'bestelddoor:' ||
    klein.startsWith('factuur#') ||
    klein.startsWith('project:') ||
    klein.startsWith('po:') ||
    klein.startsWith('uitgiftedatum') ||
    klein.startsWith('beschrijving:') ||
    klein.startsWith('datum:') ||
    klein.startsWith('betalingsinstructies') ||
    /^pagina\d+\/\d+$/.test(klein)
  )
}

/** Naam, adres, btw- en registratienummer uit de regels van één blok. */
function leesPartij(blok: Regel[]): FinqlePartij {
  let naam: string | null = null
  const adres: string[] = []
  let btwNummer: string | null = null
  let kvk: string | null = null
  for (const r of blok) {
    if (r.plat.length === 0) continue
    const btw = waardeNaLabel(r.plat, 'BTWnummer')
    if (btw !== null) {
      btwNummer = isLegeWaardeAanduiding(btw) ? null : btw
      continue
    }
    const reg = waardeNaLabel(r.plat, 'Registratienummer')
    if (reg !== null) {
      const cijfers = reg.replace(/\D/g, '')
      kvk = cijfers.length === 8 ? cijfers : null
      continue
    }
    if (r.klein.startsWith('tav:')) continue
    if (naam === null) naam = normaliseerWitruimte(r.ruw)
    else adres.push(normaliseerWitruimte(r.ruw))
  }
  return { naam, adres, btwNummer, kvk }
}

/** De regels van een blok: vanaf `start` tot het volgende blok-label. */
function blokVanaf(regels: Regel[], start: number): Regel[] {
  const uit: Regel[] = []
  for (let i = start; i < regels.length; i += 1) {
    const r = regels[i]
    if (!r) break
    if (isBlokStart(r.klein)) break
    uit.push(r)
  }
  return uit
}

const UREN_RE = new RegExp(
  `^Gewerkteuren(.+?)(${DATUM})(\\d{1,2}:\\d{2})-(\\d{1,2}:\\d{2})\\((geenpauze|\\d{1,2}:\\d{2}pauze)\\)-?(.*)$`,
  'i',
)
const UREN_NAAM_RUW_RE = new RegExp(
  `${losseRegexBron('Gewerkte uren')}\\s+(.+?)\\s+\\d{1,2}\\s*-\\s*\\d{1,2}\\s*-\\s*\\d{4}`,
  'i',
)
const DAGEN_DEEL_RE = new RegExp(`^(\\d+)dag(?:en)?\\((${DATUM})(?:t\\/m(${DATUM}))?\\)$`, 'i')
const KM_RE = new RegExp(
  `^Kilometervergoeding(.+?):(\\d{1,3}(?:\\.\\d{3}){1,3}|\\d{1,9}(?:,\\d{1,3})?)kmx€?(${BEDRAG})=€?(${BEDRAG})$`,
  'i',
)
const KM_NAAM_RUW_RE = new RegExp(`${losseRegexBron('Kilometervergoeding')}\\s+(.+?)\\s*:`, 'i')

function pauzeNaarMinuten(waarde: string): number | null {
  if (/^geenpauze$/i.test(waarde)) return 0
  const m = /^(\d{1,2}):(\d{2})pauze$/i.exec(waarde)
  if (!m) return null
  return Number.parseInt(m[1] ?? '0', 10) * 60 + Number.parseInt(m[2] ?? '0', 10)
}

interface RegelBasis {
  omschrijvingRuw: string
  totaalExCenten: number
  btwPercentage: number | null
  btwCenten: number | null
  totaalInclCenten: number | null
  eenhedenDuizendsten: number | null
  tariefCenten: number | null
}

function onbekendeRegel(basis: RegelBasis): FinqleTekstRegel {
  return {
    soort: 'OVERIG',
    omschrijvingRuw: basis.omschrijvingRuw,
    omschrijvingPlat: plet(basis.omschrijvingRuw),
    medewerkerRuw: null,
    medewerkerSleutel: null,
    datum: null,
    datumTot: null,
    startTijd: null,
    eindTijd: null,
    pauzeMinuten: null,
    tarieftypeRuw: null,
    dienstRuw: null,
    werkbonNummer: null,
    eenhedenDuizendsten: basis.eenhedenDuizendsten,
    eenheid: null,
    tariefCenten: basis.tariefCenten,
    totaalExCenten: basis.totaalExCenten,
    btwPercentage: basis.btwPercentage,
    btwCenten: basis.btwCenten,
    totaalInclCenten: basis.totaalInclCenten,
  }
}

/** "Gewerkte uren {naam} {dd-mm-jjjj} {HH:MM-HH:MM} ({pauze}) - {tarieftype}". */
function parseGewerkteUren(basis: RegelBasis): FinqleTekstRegel | null {
  const plat = plet(basis.omschrijvingRuw)
  const m = UREN_RE.exec(plat)
  if (!m) return null
  const datum = parseNlDatum(m[2])
  const start = normaliseerKlokTijd(m[3])
  const eind = normaliseerKlokTijd(m[4])
  if (!datum || !start || !eind) return null
  const naamRuw = UREN_NAAM_RUW_RE.exec(basis.omschrijvingRuw)?.[1] ?? m[1] ?? ''
  // Het tarieftype staat in de ruwe tekst na ") -".
  const tarieftypeRuw = /\)\s*-\s*(.*)$/.exec(basis.omschrijvingRuw)?.[1] ?? m[6] ?? ''
  return {
    ...onbekendeRegel(basis),
    soort: 'UREN',
    medewerkerRuw: normaliseerWitruimte(naamRuw),
    medewerkerSleutel: naamSleutel(m[1] ?? ''),
    datum,
    startTijd: start,
    eindTijd: eind,
    pauzeMinuten: pauzeNaarMinuten(m[5] ?? ''),
    tarieftypeRuw: normaliseerWitruimte(tarieftypeRuw) || null,
    eenheid: 'uur',
  }
}

/**
 * "{werkbon} - {tarieftype} - {naam} [- {dienst}] - {n} dag(en) ({van} [t/m {tot}])".
 * De delen zijn gescheiden door " - " (witruimte aan BEIDE kanten); een streepje
 * binnen een woord ("2-ploegendienst", "31-08-2026") heeft dat niet.
 */
function parseDagtarief(basis: RegelBasis): FinqleTekstRegel | null {
  const delen = basis.omschrijvingRuw.split(/\s+-\s+/).map((d) => d.trim())
  if (delen.length < 4) return null
  const eerste = plet(delen[0] ?? '')
  if (!new RegExp(`^${WERKBON}$`).test(eerste)) return null
  const laatste = DAGEN_DEEL_RE.exec(plet(delen[delen.length - 1] ?? ''))
  if (!laatste) return null
  const van = parseNlDatum(laatste[2])
  const tot = laatste[3] ? parseNlDatum(laatste[3]) : null
  if (!van || (laatste[3] && !tot)) return null
  const naamRuw = delen[2] ?? ''
  const dienst = delen.slice(3, delen.length - 1).join(' - ')
  return {
    ...onbekendeRegel(basis),
    soort: 'DAGTARIEF',
    medewerkerRuw: normaliseerWitruimte(naamRuw),
    medewerkerSleutel: naamSleutel(naamRuw),
    datum: van,
    datumTot: tot && tot !== van ? tot : null,
    tarieftypeRuw: normaliseerWitruimte(delen[1] ?? '') || null,
    dienstRuw: dienst ? normaliseerWitruimte(dienst) : null,
    werkbonNummer: eerste.toUpperCase(),
    eenheid: 'dag',
  }
}

/** Kostenregel: "{datum}{werkbon} - Kilometervergoeding {naam}: {km} km x € {tarief} = € {bedrag}". */
function parseKosten(datumIso: string, restRuw: string, basis: RegelBasis): FinqleTekstRegel {
  const regel: FinqleTekstRegel = {
    ...onbekendeRegel({ ...basis, omschrijvingRuw: restRuw }),
    soort: 'KOSTEN',
    datum: datumIso,
  }
  let rest = restRuw
  const delen = restRuw.split(/\s+-\s+/)
  const eerste = plet(delen[0] ?? '')
  if (delen.length >= 2 && new RegExp(`^${WERKBON}$`).test(eerste)) {
    regel.werkbonNummer = eerste.toUpperCase()
    rest = delen.slice(1).join(' - ')
  }
  const km = KM_RE.exec(plet(rest))
  if (km) {
    const naamRuw = KM_NAAM_RUW_RE.exec(rest)?.[1] ?? km[1] ?? ''
    regel.medewerkerRuw = normaliseerWitruimte(naamRuw)
    regel.medewerkerSleutel = naamSleutel(km[1] ?? '')
    regel.tarieftypeRuw = 'Kilometervergoeding'
    const kmDuizendsten = parseNlGetalNaarDuizendsten(km[2])
    const tarief = parseNlBedragNaarCenten(km[3])
    // Alleen als km x tarief op het regelbedrag uitkomt nemen we km als eenheid over.
    if (
      kmDuizendsten !== null &&
      tarief !== null &&
      Math.abs(Math.round((kmDuizendsten * tarief) / 1000) - basis.totaalExCenten) <= 1
    ) {
      regel.eenhedenDuizendsten = kmDuizendsten
      regel.eenheid = 'km'
      regel.tariefCenten = tarief
    }
  }
  return regel
}

function parseEenhedenRegel(basis: RegelBasis, parsers: ReadonlySet<ExtractieRegelParser>): FinqleTekstRegel {
  if (parsers.has('FINQLE_GEWERKTE_UREN')) {
    const uren = parseGewerkteUren(basis)
    if (uren) return uren
  }
  if (parsers.has('FINQLE_DAGTARIEF')) {
    const dag = parseDagtarief(basis)
    if (dag) return dag
  }
  return onbekendeRegel(basis)
}

/** Plafonds: tekst uit een open mailbox mag de lezer nooit laten doorlopen. */
const MAX_REGELS = 2000
const MAX_OMSCHRIJVING = 600
/**
 * Een regel (zonder witruimte) langer dan dit wordt overgeslagen: geen regel van
 * deze opmaak komt in de buurt, en een lange regel maakt een regex die naar het
 * eind van de regel zoekt kwadratisch duur.
 */
const MAX_REGEL_LENGTE = 500
/** Een omschrijving loopt over hooguit zoveel tekstregels voor de bedragen volgen. */
const MAX_BUFFER_REGELS = 12

export const FINQLE_REGEL_PARSERS_STANDAARD: readonly ExtractieRegelParser[] = [
  'FINQLE_GEWERKTE_UREN',
  'FINQLE_DAGTARIEF',
  'FINQLE_KOSTEN',
]

/**
 * Lees een factuur in de Finqle-opmaak uit de PDF-tekst. Wat niet gevonden wordt
 * blijft null: de aanroeper (naverwerking) valt dan terug op de AI-waarde.
 */
export function leesFinqleTekst(
  tekst: string,
  opties: { regelParsers?: readonly ExtractieRegelParser[] } = {},
): FinqleTekstExtractie {
  const parsers = new Set<ExtractieRegelParser>(opties.regelParsers ?? FINQLE_REGEL_PARSERS_STANDAARD)
  const regels: Regel[] = splitsRegels(tekst)
    .slice(0, MAX_REGELS)
    .map((ruw) => {
      const plat = plet(ruw)
      // Te lang voor deze opmaak: als lege regel behandelen (zie MAX_REGEL_LENGTE).
      // De tekst blijft alleen beschikbaar om het einde van de paginavoet te zien.
      return plat.length > MAX_REGEL_LENGTE
        ? { ruw: '', plat: '', klein: '', lang: plat.toLowerCase() }
        : { ruw, plat, klein: plat.toLowerCase() }
    })

  const uit: FinqleTekstExtractie = {
    factuurnummer: null,
    project: null,
    po: null,
    poAanwezig: false,
    factuurdatum: null,
    vervaldatum: null,
    betalingstermijnDagen: null,
    debiteur: null,
    besteldDoor: null,
    afzender: null,
    btwRijen: [],
    totaalTeBetalenCenten: null,
    totalen: { exCenten: null, btwCenten: null, inclCenten: null },
    subtotaalEenheden: null,
    subtotaalKosten: null,
    regels: [],
    factoring: false,
    factoringIban: null,
    factoringTenNameVan: null,
    consistentie: {
      totalenSluiten: null,
      totaalTeBetalenKlopt: null,
      regelsSluiten: null,
      regelBedragenKloppen: null,
    },
  }

  let voetFactuurnummer: string | null = null
  type Modus = 'GEEN' | 'EENHEDEN' | 'KOSTEN'
  let modus: Modus = 'GEEN'
  let hervat: Modus = 'GEEN'
  let inVoet = false
  let buffer: string[] = []

  for (let i = 0; i < regels.length; i += 1) {
    const r = regels[i]
    if (!r) continue
    const { plat, klein } = r

    // ── Paginavoet: afzenderblok + betalingsinstructies; tabellen pauzeren. ──
    if (/^pagina\d+\/\d+$/.test(klein)) {
      hervat = modus === 'GEEN' ? hervat : modus
      modus = 'GEEN'
      inVoet = true
      // De buffer blijft staan: een omschrijving die over de paginagrens loopt
      // (eerste tekstregel onderaan de pagina, de rest en de bedragen op de
      // volgende) wordt na de voet weer aangevuld. Voetregels komen er niet in.
      if (uit.afzender === null) {
        const blok = blokVanaf(regels, i + 1)
        const partij = leesPartij(blok)
        if (partij.naam !== null) uit.afzender = partij
      }
      continue
    }
    if (inVoet) {
      if (klein.startsWith('factuur#') && voetFactuurnummer === null) {
        const nr = (waardeNaLabel(plat, 'Factuur#') ?? '').replace(/[.,;]+$/, '')
        if (/^[A-Za-z0-9._/-]+$/.test(nr)) voetFactuurnummer = nr
      }
      // De voet eindigt na de factoringtekst ("o.v.v. het factuurnummer ..."),
      // ook als die alinea als een lange regel in de tekstlaag staat.
      if (klein.includes('o.v.v.hetfactuurnummer') || r.lang?.includes('o.v.v.hetfactuurnummer') === true) {
        inVoet = false
        modus = hervat
      }
      // Ook zonder factoringtekst eindigt de voet zodra er weer inhoud komt:
      // een tabelkop, de btw-tabel of een totaalregel (pagina 2 van FA2026-20439).
      if (
        klein === KOP_EENHEDEN ||
        klein === KOP_KOSTEN ||
        klein.startsWith('btw-tarief') ||
        klein.startsWith('totaaltebetalen') ||
        klein.startsWith('subtotalen(') ||
        klein.startsWith('totalen:') ||
        BTW_RIJ_RE.test(plat)
      ) {
        inVoet = false
        if (modus === 'GEEN') modus = hervat
      } else {
        continue
      }
    }

    // ── Tabelkoppen ──
    // Een kop die op de volgende pagina HERHAALD wordt (zelfde tabel loopt door)
    // laat de buffer staan; een andere tabel begint met een lege buffer.
    if (klein === KOP_EENHEDEN) {
      if (modus !== 'EENHEDEN') buffer = []
      modus = 'EENHEDEN'
      continue
    }
    if (klein === KOP_KOSTEN) {
      if (modus !== 'KOSTEN') buffer = []
      modus = 'KOSTEN'
      continue
    }

    // ── Totaalregels (sluiten een tabel af) ──
    if (klein.startsWith('subtotalen(')) {
      const drie = DRIE_BEDRAGEN_RE.exec(plat)
      const totalen: FinqleTotalen = {
        exCenten: centen(drie?.[1]),
        btwCenten: centen(drie?.[2]),
        inclCenten: centen(drie?.[3]),
      }
      if (klein.startsWith('subtotalen(eenheden)')) {
        const voor = drie ? plat.slice(0, drie.index) : plat
        const eenheden = /:(-?[\d.]{1,15}(?:,\d{1,3})?)$/.exec(voor)?.[1]
        uit.subtotaalEenheden = {
          ...totalen,
          eenhedenDuizendsten: parseNlGetalNaarDuizendsten(eenheden ?? null),
        }
      } else {
        uit.subtotaalKosten = totalen
      }
      modus = 'GEEN'
      hervat = 'GEEN'
      buffer = []
      continue
    }
    if (klein.startsWith('totalen:')) {
      const drie = DRIE_BEDRAGEN_RE.exec(plat)
      if (drie) {
        uit.totalen = {
          exCenten: centen(drie[1]),
          btwCenten: centen(drie[2]),
          inclCenten: centen(drie[3]),
        }
      }
      modus = 'GEEN'
      hervat = 'GEEN'
      buffer = []
      continue
    }
    if (klein.startsWith('totaaltebetalen')) {
      const m = TOTAAL_TE_BETALEN_RE.exec(plat)
      if (m) uit.totaalTeBetalenCenten = centen(m[1])
      continue
    }
    const btwRij = BTW_RIJ_RE.exec(plat)
    if (btwRij) {
      const pct = percentage(btwRij[1])
      const grondslag = centen(btwRij[2])
      const btw = centen(btwRij[3])
      const incl = centen(btwRij[4])
      if (pct !== null && grondslag !== null && btw !== null && incl !== null) {
        uit.btwRijen.push({ percentage: pct, grondslagCenten: grondslag, btwCenten: btw, inclCenten: incl })
      }
      continue
    }

    // ── Tabelregels ──
    if (modus === 'EENHEDEN') {
      const m = EENHEDEN_BEDRAGEN_RE.exec(plat)
      if (!m) {
        if (plat.length > 0) {
          // Zoveel tekstregels zonder bedragen is geen omschrijving meer.
          if (buffer.length >= MAX_BUFFER_REGELS) buffer = []
          buffer.push(r.ruw)
        }
        continue
      }
      const totaalEx = centen(m[3])
      if (totaalEx === null) {
        buffer = []
        continue
      }
      const omschrijvingRuw = normaliseerWitruimte(buffer.join(' ')).slice(0, MAX_OMSCHRIJVING)
      buffer = []
      uit.regels.push(
        parseEenhedenRegel(
          {
            omschrijvingRuw,
            eenhedenDuizendsten: parseNlGetalNaarDuizendsten(m[1]),
            tariefCenten: centen(m[2]),
            totaalExCenten: totaalEx,
            btwPercentage: percentage(m[4]),
            btwCenten: centen(m[5]),
            totaalInclCenten: centen(m[6]),
          },
          parsers,
        ),
      )
      continue
    }
    if (modus === 'KOSTEN') {
      if (plat.length === 0) continue
      if (buffer.length >= MAX_BUFFER_REGELS) buffer = []
      buffer.push(r.ruw)
      const samen = normaliseerWitruimte(buffer.join(' ')).slice(0, MAX_OMSCHRIJVING * 2)
      const samenPlat = plet(samen)
      const staart = KOSTEN_STAART_RE.exec(samenPlat)
      if (!staart) continue
      buffer = []
      const totaalEx = centen(staart[1])
      const datumM = new RegExp(`^(${DATUM})`).exec(samenPlat)
      const datum = datumM ? parseNlDatum(datumM[1]) : null
      if (totaalEx === null || !datum) continue
      // Ruwe omschrijving: zonder de datum vooraan en zonder de bedragenstaart.
      const zonderDatum = samen.replace(/^\s*\d{1,2}\s*-\s*\d{1,2}\s*-\s*\d{4}\s*/, '')
      const staartRuw = new RegExp(
        `\\s*${losseBedrag(staart[1] ?? '')}\\s*${losseRegexBron(`${staart[2] ?? ''}%`)}.*$`,
      )
      const omschrijvingRuw = normaliseerWitruimte(zonderDatum.replace(staartRuw, ''))
      const basis: RegelBasis = {
        omschrijvingRuw,
        eenhedenDuizendsten: null,
        tariefCenten: null,
        totaalExCenten: totaalEx,
        btwPercentage: percentage(staart[2]),
        btwCenten: centen(staart[3]),
        totaalInclCenten: centen(staart[4]),
      }
      uit.regels.push(
        parsers.has('FINQLE_KOSTEN')
          ? parseKosten(datum, omschrijvingRuw, basis)
          : { ...onbekendeRegel(basis), datum },
      )
      continue
    }

    // ── Kop ──
    if (klein.startsWith('factuur#:')) {
      const nr = (waardeNaLabel(plat, 'Factuur#') ?? '').replace(/[.,;]+$/, '')
      if (uit.factuurnummer === null && /^[A-Za-z0-9._/-]+$/.test(nr)) uit.factuurnummer = nr
      continue
    }
    if (klein.startsWith('project:')) {
      const v = waardeNaLabel(plat, 'Project') ?? ''
      uit.project = isLegeWaardeAanduiding(v) ? null : normaliseerWitruimte(r.ruw.replace(/^[^:]*:/, ''))
      continue
    }
    if (klein.startsWith('po:')) {
      const v = waardeNaLabel(plat, 'PO') ?? ''
      uit.po = isLegeWaardeAanduiding(v) ? null : v
      uit.poAanwezig = true
      continue
    }
    if (klein.startsWith('uitgiftedatum')) {
      uit.factuurdatum = parseNlDatum(waardeNaLabel(plat, 'Uitgiftedatum')) ?? uit.factuurdatum
      continue
    }
    if (klein.startsWith('vervaldatum')) {
      uit.vervaldatum = parseNlDatum(waardeNaLabel(plat, 'Vervaldatum')) ?? uit.vervaldatum
      continue
    }
    if (klein.startsWith('betalingstermijn')) {
      const m = /^Betalingstermijn:?(\d{1,3})dag/i.exec(plat)
      if (m) uit.betalingstermijnDagen = Number.parseInt(m[1] ?? '', 10)
      continue
    }

    // ── Partijen ──
    if (klein === 'debiteur:' && uit.debiteur === null) {
      uit.debiteur = leesPartij(blokVanaf(regels, i + 1))
      continue
    }
    if (klein === 'bestelddoor:' && uit.besteldDoor === null) {
      const blok = blokVanaf(regels, i + 1)
      const tav = blok.find((b) => b.klein.startsWith('tav:'))
      const tavWaarde = tav ? normaliseerWitruimte(tav.ruw.replace(/^[^:]*:/, '')) : ''
      uit.besteldDoor = { naam: leesPartij(blok).naam, tav: tavWaarde || null }
      continue
    }
  }

  // Het kopnummer wint; staat er geen kop, dan het nummer uit de paginavoet.
  if (uit.factuurnummer === null) uit.factuurnummer = voetFactuurnummer

  // ── Factoring: de zin met het IBAN van de factoringmaatschappij ──
  const lopend = normaliseerWitruimte(tekst.slice(0, 200_000))
  uit.factoring = pletKlein(lopend).includes('maaktgebruikvanfactoring')
  const iban = /IBAN\s+([A-Z]{2}\s?\d{2}(?:\s?[A-Z0-9]){11,30})\s+ten\s+name\s+van\s+([^.]{2,80}?\.?(?:B\.V\.|N\.V\.)?)(?=\s+te\s|\s+o\.v\.v\.|,|$)/i.exec(
    lopend,
  )
  if (iban) {
    uit.factoringIban = plet(iban[1] ?? '').toUpperCase()
    uit.factoringTenNameVan = normaliseerWitruimte(iban[2] ?? '') || null
  }

  uit.consistentie = controleerConsistentie(uit)
  return uit
}

/** Regex-bron voor een bedrag met losse witruimte tussen de tekens. */
function losseBedrag(bedrag: string): string {
  return losseRegexBron(bedrag)
}

function sluit(a: number, b: number): boolean {
  return Math.abs(a - b) <= 1
}

/** De eindtotalen zoals de tekst ze geeft: excl / btw / incl. */
export function finqleEindtotalen(e: Pick<FinqleTekstExtractie, 'btwRijen' | 'totalen' | 'totaalTeBetalenCenten'>): FinqleTotalen {
  const heeftRijen = e.btwRijen.length > 0
  const exUitRijen = heeftRijen ? e.btwRijen.reduce((s, r) => s + r.grondslagCenten, 0) : null
  const btwUitRijen = heeftRijen ? e.btwRijen.reduce((s, r) => s + r.btwCenten, 0) : null
  const inclUitRijen = heeftRijen ? e.btwRijen.reduce((s, r) => s + r.inclCenten, 0) : null
  return {
    exCenten: exUitRijen ?? e.totalen.exCenten,
    btwCenten: btwUitRijen ?? e.totalen.btwCenten,
    inclCenten: e.totaalTeBetalenCenten ?? inclUitRijen ?? e.totalen.inclCenten,
  }
}

function controleerConsistentie(e: FinqleTekstExtractie): FinqleConsistentie {
  const eind = finqleEindtotalen(e)
  const totalenSluiten =
    eind.exCenten !== null && eind.btwCenten !== null && eind.inclCenten !== null
      ? sluit(eind.exCenten + eind.btwCenten, eind.inclCenten) &&
        // De regel "Totalen:" moet, als hij er staat, hetzelfde zeggen.
        (e.totalen.exCenten === null || sluit(e.totalen.exCenten, eind.exCenten)) &&
        (e.totalen.btwCenten === null || sluit(e.totalen.btwCenten, eind.btwCenten)) &&
        (e.totalen.inclCenten === null || sluit(e.totalen.inclCenten, eind.inclCenten))
      : null
  const inclUitRijen =
    e.btwRijen.length > 0 ? e.btwRijen.reduce((s, r) => s + r.inclCenten, 0) : e.totalen.inclCenten
  const totaalTeBetalenKlopt =
    e.totaalTeBetalenCenten !== null && inclUitRijen !== null
      ? sluit(e.totaalTeBetalenCenten, inclUitRijen)
      : null
  const regelsSluiten =
    e.regels.length > 0 && eind.exCenten !== null
      ? sluit(
          e.regels.reduce((s, r) => s + r.totaalExCenten, 0),
          eind.exCenten,
        )
      : null
  const metTarief = e.regels.filter((r) => r.eenhedenDuizendsten !== null && r.tariefCenten !== null)
  const regelBedragenKloppen =
    metTarief.length > 0
      ? metTarief.every((r) =>
          sluit(Math.round(((r.eenhedenDuizendsten ?? 0) * (r.tariefCenten ?? 0)) / 1000), r.totaalExCenten),
        )
      : null
  return { totalenSluiten, totaalTeBetalenKlopt, regelsSluiten, regelBedragenKloppen }
}
